/**
 * Carries paid-ad attribution across the hop to medsparecord.com.
 *
 * The ads land on lakewoodmedspa.com but the conversion — the actual booking —
 * happens on medsparecord.com. Meta's `_fbc` cookie is first-party to whichever
 * domain set it, so the booking site cannot read the click ID this site stored.
 * If the click ID is not carried in the link itself it is simply gone, and the
 * campaign that paid for the click never learns the booking happened.
 *
 * `/lp/tox-membership-dallas` (server-rendered separately) already does this.
 * Everything rendered by the React app did not, which is every other page.
 */

const ATTRIBUTION_KEYS = [
  // Click identifiers — these are what make a conversion deterministically
  // matchable back to the ad that paid for it.
  "fbclid",
  "gclid",
  "wbraid",
  "gbraid",
  "msclkid",
  "ttclid",
  // OpenAI's click reference for ChatGPT Ads. Documented as appended to the
  // landing URL and to be preserved through navigation; never invent or reuse one.
  "oppref",
  // Campaign context — what the reporting is grouped by.
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const STORAGE_KEY = "lms_ad_attribution";

type Attribution = Record<string, string>;

function readStored(): Attribution {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Attribution) : {};
  } catch {
    // Private browsing, disabled storage, or corrupt JSON. A visit that cannot
    // remember its attribution must still be able to book.
    return {};
  }
}

function writeStored(value: Attribution): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Non-fatal: decoration falls back to whatever this page's URL still carries.
  }
}

/**
 * Records any attribution present on the current URL.
 *
 * Merges rather than replaces: the visitor lands on the ad URL once, then
 * navigates the SPA to pages whose URLs carry nothing. Replacing on every
 * navigation would erase the click ID before they ever reach a booking link.
 */
const GOOGLE_CLICK_KEYS = ["gclid", "gbraid", "wbraid"] as const;
const OPENAI_SOURCES = ["openai", "chatgpt"];

export function captureAdAttribution(search: string = window.location.search): Attribution {
  const incoming = new URLSearchParams(search);
  const stored = readStored();
  let changed = false;

  // A click arriving now outranks one already in storage. Merging the two lets
  // a visitor who clicked a Google ad earlier and a Meta ad now arrive at the
  // booking page carrying both ids, so the Meta-paid booking can be credited to
  // the older Google click. Whichever channel brought them this time wins, and
  // the other channel's click id -- plus, on a Meta arrival, the previous
  // campaign's UTMs -- is dropped rather than carried along.
  const source = (incoming.get("utm_source") ?? "").toLowerCase();
  const metaNow =
    !!incoming.get("fbclid") || ["meta", "facebook", "fb", "ig", "instagram"].includes(source);
  const googleNow =
    GOOGLE_CLICK_KEYS.some((key) => !!incoming.get(key)) ||
    ["google", "adwords", "gads", "googleads"].includes(source);
  // ChatGPT Ads is its own channel. It used to count as "no fresh click", so an
  // OpenAI visit kept whatever Google/Meta ids were already in storage and
  // handed medsparecord.com all of them at once.
  const openaiNow = !!incoming.get("oppref") || OPENAI_SOURCES.includes(source);
  const storedWasOpenai =
    stored.oppref !== undefined || OPENAI_SOURCES.includes((stored.utm_source ?? "").toLowerCase());

  const drop = (key: (typeof ATTRIBUTION_KEYS)[number]) => {
    if (stored[key] === undefined) return;
    delete stored[key];
    changed = true;
  };
  // Only clear the previous campaign's UTMs when this arrival is itself a
  // tagged campaign link. A bare click id -- an organic share, a story link --
  // carries no campaign of its own, and wiping the UTMs there would throw
  // away the visitor's real campaign context.
  const dropStaleCampaign = () => {
    if (!ATTRIBUTION_KEYS.some((key) => key.startsWith("utm_") && incoming.get(key))) return;
    for (const key of ATTRIBUTION_KEYS) {
      if (key.startsWith("utm_") && !incoming.get(key)) drop(key);
    }
  };

  if (metaNow) {
    for (const key of GOOGLE_CLICK_KEYS) drop(key);
    dropStaleCampaign();
  }
  if (googleNow) {
    drop("fbclid");
    // Google's own campaign handling is unchanged; only a leftover OpenAI
    // campaign needs clearing so utm_content=chatgpt* cannot ride a Google booking.
    if (storedWasOpenai) dropStaleCampaign();
  }
  if (openaiNow) {
    for (const key of GOOGLE_CLICK_KEYS) drop(key);
    drop("fbclid");
    dropStaleCampaign();
  }
  if (metaNow || googleNow) drop("oppref");

  for (const key of ATTRIBUTION_KEYS) {
    const value = incoming.get(key);
    if (value && value !== stored[key]) {
      stored[key] = value;
      changed = true;
    }
  }

  if (changed) writeStored(stored);
  return stored;
}

export function getAdAttribution(): Attribution {
  return readStored();
}

/**
 * Resolves an href without assuming a DOM. Booking links are absolute, so the
 * document origin is only a fallback for relative hrefs.
 */
function parseUrl(href: string): URL | null {
  try {
    return new URL(href);
  } catch {
    // Not absolute.
  }
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : undefined;
    return origin ? new URL(href, origin) : null;
  } catch {
    return null;
  }
}

/** True for the booking destinations this site hands visitors off to. */
export function isBookingDestination(url: URL): boolean {
  return (
    (url.hostname === "medsparecord.com" || url.hostname === "www.medsparecord.com") &&
    url.pathname.startsWith("/book")
  );
}

/**
 * Appends stored attribution to a booking URL.
 *
 * Never overwrites a parameter the link already sets — a hand-built campaign
 * link is more specific than the ambient session, and clobbering it would
 * silently retarget the link. Returns the input unchanged when there is
 * nothing to add, so callers can compare and skip a no-op write.
 */
export function decorateBookingUrl(
  href: string,
  attribution: Attribution = getAdAttribution(),
): string {
  const url = parseUrl(href);
  if (!url || !isBookingDestination(url)) return href;

  let changed = false;
  for (const [key, value] of Object.entries(attribution)) {
    if (!value || url.searchParams.has(key)) continue;
    url.searchParams.set(key, value);
    changed = true;
  }

  return changed ? url.toString() : href;
}
